import React from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClipResultCard from "../../videoStudio/clipping/components/ClipResultCard";
import ShareClipModal from "./ShareClipModal";

function formatDate(dt) {
  if (!dt) return "";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

export default function ClipProjectGroup({ project, lang, defaultExpanded = true }) {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [shareClip, setShareClip] = React.useState(null);

  const clips = project?.clips || [];
  const title =
    project?.source_title ||
    project?.source_url ||
    lang?.clippingSourceVideo ||
    "Source video";
  const windowLabel =
    project?.start_sec != null && project?.end_sec != null
      ? `${project.start_sec}s – ${project.end_sec}s`
      : null;

  const handleCopyCaption = (text) => {
    const value = String(text || "").trim();
    if (!value) return;
    navigator.clipboard?.writeText(value);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            {title}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(project?.created_at)}
            </Typography>
            {windowLabel && (
              <Chip size="small" label={windowLabel} variant="outlined" sx={{ height: 20 }} />
            )}
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${clips.length} ${lang?.aiClippingClipsLabel || "clips"}`}
              sx={{ height: 20 }}
            />
          </Stack>
        </Box>
        <IconButton
          size="small"
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {clips.map((clip) => (
              <ClipResultCard
                key={clip.id || clip.ssemble_clip_id}
                clip={{
                  ...clip,
                  id: clip.ssemble_clip_id || clip.id,
                  viral_score: clip.viral_score,
                  video_url: clip.video_url,
                  duration: clip.duration ?? clip.duration_sec,
                }}
                onCopyCaption={handleCopyCaption}
                onShare={() => setShareClip(clip)}
                lang={lang}
              />
            ))}
          </Box>
        </Box>
      </Collapse>

      <ShareClipModal
        open={Boolean(shareClip)}
        onClose={() => setShareClip(null)}
        clip={shareClip}
        lang={lang}
      />
    </Box>
  );
}
